import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

const EMPTY_PAY = { amount: "", date: "", payment_method: "", account_id: "", reference: "", comment: "", justificatif: "" };

const InfoLine = ({ label, value, strong, red }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono-num text-right ${strong ? "font-bold" : "font-medium"} ${red ? "text-red-600" : "text-slate-800"}`}>{value}</span>
  </div>
);

export function SupplierPaymentDialog({ debt, onClose, onPaid }) {
  const [accounts, setAccounts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState(EMPTY_PAY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!debt) return;
    setForm({ ...EMPTY_PAY, amount: String(debt.remaining), date: todayISO(), payment_method: debt.payment_method || "" });
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMethods(r.data.payment_methods || [])).catch(() => {});
  }, [debt]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Montant du paiement invalide");
    if (amt > debt.remaining) return toast.error("Le montant dépasse le reste à payer");
    setBusy(true);
    try {
      await api.post(`/purchases/${debt.id}/pay`, {
        amount: amt, account_id: form.account_id || null, date: form.date || null,
        payment_method: form.payment_method || null, reference: form.reference,
        comment: form.comment, justificatif: form.justificatif,
      });
      toast.success("Paiement fournisseur enregistré");
      onClose();
      onPaid?.();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!debt} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="supplier-payment-dialog">
        <DialogHeader><DialogTitle>Payer la dette — {debt?.purchase_number}</DialogTitle></DialogHeader>
        {debt && (
          <div className="space-y-4">
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-3">
              <InfoLine label="Fournisseur" value={debt.supplier_name || "—"} strong />
              <InfoLine label="Référence de la dette" value={debt.purchase_number} />
              <InfoLine label="Facture concernée" value={debt.purchase_number} />
              {debt.vehicle_label && <InfoLine label="Véhicule concerné" value={debt.vehicle_label} />}
              <InfoLine label="Montant total de la dette" value={fmtMoney(debt.price_fcfa)} />
              <InfoLine label="Déjà payé" value={fmtMoney(debt.paid_amount)} />
              <InfoLine label="Reste à payer" value={fmtMoney(debt.remaining)} strong red />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Montant du paiement (FCFA) *</Label><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="debt-pay-amount" /></div>
              <div><Label>Date de paiement</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="debt-pay-date" /></div>
              <div><Label>Mode de paiement</Label>
                <Select value={form.payment_method || undefined} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger data-testid="debt-pay-method"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Compte utilisé</Label>
                <Select value={form.account_id || undefined} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="debt-pay-account"><SelectValue placeholder="Aucun (hors caisse)" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun (hors caisse)</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Référence du paiement</Label><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="N° chèque, référence virement…" data-testid="debt-pay-reference" /></div>
              <div className="sm:col-span-2"><Label>Commentaire</Label><Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)} rows={2} data-testid="debt-pay-comment" /></div>
              <div className="sm:col-span-2"><Label>Justificatif (n° de pièce / reçu)</Label><Input value={form.justificatif} onChange={(e) => set("justificatif", e.target.value)} data-testid="debt-pay-justificatif" /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="debt-pay-submit">{busy ? "Enregistrement…" : "Confirmer le paiement"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentHistoryDialog({ purchase, onClose }) {
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    if (!purchase) return;
    setPayments(null);
    api.get(`/purchases/${purchase.id}/payments`).then((r) => setPayments(r.data)).catch(() => setPayments([]));
  }, [purchase]);

  return (
    <Dialog open={!!purchase} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-history-dialog">
        <DialogHeader><DialogTitle>Historique des paiements — {purchase?.purchase_number}</DialogTitle></DialogHeader>
        {payments === null ? (
          <p className="text-sm text-slate-400">Chargement…</p>
        ) : payments.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="payment-history-empty">Aucun paiement enregistré pour cette dette.</p>
        ) : (
          <div className="space-y-2">
            {payments.map((p) => (
              <div key={p.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`payment-history-row-${p.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">{fmtDate(p.date)}</span>
                  <span className="font-mono-num font-bold text-emerald-600">{fmtMoney(p.amount)}</span>
                </div>
                <p className="text-xs text-slate-500">
                  {p.payment_method || "—"}{p.account_name ? ` · ${p.account_name}` : ""}{p.reference ? ` · Réf ${p.reference}` : ""}{p.created_by ? ` · par ${p.created_by}` : ""}
                </p>
                {p.comment && <p className="text-xs italic text-slate-400">{p.comment}</p>}
                {p.justificatif && <p className="text-xs text-slate-400">Pièce : {p.justificatif}</p>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
